import {useEffect, useState} from 'react';
import useWebSocket, {ReadyState} from 'react-use-websocket';

import {autorun} from 'mobx';

import {createTrace} from '@yourcoach/trace';

import {currentUserStore} from '../../api/user';
import {apiRequest} from '../../api/utils/apiRequest';

const READY_STATE_NAMES: Record<number, string> = {
  [ReadyState.CLOSED]: 'CLOSED',
  [ReadyState.CLOSING]: 'CLOSING',
  [ReadyState.CONNECTING]: 'CONNECTING',
  [ReadyState.OPEN]: 'OPEN',
  [ReadyState.UNINSTANTIATED]: 'UNINSTANTIATED',
};

const traceWs = createTrace('ws');

let wsMessageSeq = 0;

/**
 * Each raw message's own `seq`. `lastJsonMessage` is derived from `lastMessage`
 * (the same `MessageEvent`), so `ws emit` can report the seq of the message it
 * emits: a seq present in `ws raw` but missing from `ws emit` was batched away.
 */
const wsMessageSeqs = new WeakMap<MessageEvent, number>();

/** `ws` trace fields for a message. */
export const getWsTraceData = (message: unknown, seq?: number) => {
  const shape = message as null | {
    conference?: {_id?: unknown};
    event?: {type?: unknown};
  };

  return {
    confId: shape?.conference?._id,
    seq,
    type: shape?.event?.type,
  };
};

let lastRawEvent: MessageEvent | null = null;

const traceRawMessage = (event: MessageEvent) => {
  // With `share: true` every subscriber gets the same event: count it once.
  if (event === lastRawEvent) return;

  lastRawEvent = event;
  wsMessageSeq += 1;
  wsMessageSeqs.set(event, wsMessageSeq);

  const {data} = event;

  if (!traceWs.enabled()) return;

  let message: unknown = null;

  try {
    message = typeof data === 'string' ? JSON.parse(data) : null;
  } catch {
    // Not JSON: traced with empty type/confId.
  }

  traceWs('raw', getWsTraceData(message, wsMessageSeq));
};

export const useWS = (url: string, shouldConnect = true) => {
  const [shouldConnectState, setShouldConnect] = useState(
    shouldConnect && !!currentUserStore.user,
  );
  const [userExists, setUserExists] = useState(!!currentUserStore.user);
  const [accessTokenState, setAccessTokenState] = useState({
    expires: 0,
    token: '',
  });

  useEffect(() => {
    const dispose = autorun(() => {
      setUserExists(!!currentUserStore.user);
    });

    return dispose;
  }, []);

  useEffect(() => {
    setShouldConnect(shouldConnect && userExists);
  }, [shouldConnect, userExists]);

  const {
    // TODO: Add WsMessage TS type
    lastJsonMessage: lastWsMessage,
    lastMessage,
    readyState,
    sendMessage,
  } = useWebSocket(
    url,
    {
      onClose: event =>
        traceWs('close', {code: event.code, reason: event.reason}),
      onError: () => traceWs('error'),
      onMessage: traceRawMessage,
      onOpen: () => traceWs('open'),
      reconnectAttempts: 10 * 5 * 12, // 10 mins
      reconnectInterval: 5000, // each 5 secs
      share: true,
      shouldReconnect: () => !!currentUserStore.user,
    },
    shouldConnectState,
  );

  useEffect(() => {
    traceWs('state', {
      readyState: READY_STATE_NAMES[readyState] ?? readyState,
      shouldConnect: shouldConnectState,
      userExists,
    });
  }, [readyState, shouldConnectState, userExists]);

  useEffect(() => {
    const getAccessToken = () => {
      const {
        accessToken,
        accessTokenExpires,
        // @ts-expect-error: It's "injected" via Object.defineProperty
      } = apiRequest.__getAccessToken();

      setAccessTokenState({
        expires: accessTokenExpires,
        token: accessToken,
      });
    };

    // @ts-expect-error: It's "injected" via Object.defineProperty
    apiRequest.__onAccessTokenChange(() => getAccessToken());

    getAccessToken();
  }, []);

  useEffect(() => {
    let timeout: number;

    const sendAccessToken = () => {
      clearTimeout(timeout);

      const {expires: accessTokenExpires, token: accessToken} =
        accessTokenState;

      if (
        readyState === ReadyState.OPEN &&
        accessTokenState &&
        Date.now() < accessTokenExpires
      ) {
        sendMessage(accessToken);
        // Never trace the token itself.
        traceWs('token.sent');
      } else {
        timeout = +setTimeout(() => {
          sendAccessToken();
        }, 1000);
      }
    };

    sendAccessToken();

    return () => {
      clearTimeout(timeout);
    };
  }, [accessTokenState, readyState, sendMessage]);

  const lastWsMessageSeq = lastMessage
    ? wsMessageSeqs.get(lastMessage)
    : undefined;

  return {lastWsMessage, lastWsMessageSeq, sendMessage};
};
